import {
  getInAppBrowserType,
  getOpenExternalPlan,
  isMetaInAppBrowser,
  type InAppBrowserType,
} from "@/lib/utils/in-app-browser";

export type OAuthProvider = "google" | "apple" | "kakao" | "naver" | "line";

export type InAppPrecheckResult =
  | { action: "proceed" }
  | { action: "auto-redirect"; url: string }
  | { action: "show-guide" };

/**
 * Provider × 인앱 환경 조합으로 "인앱 안에서 시도하면 위험한가" 판단.
 *
 * 위험 케이스:
 * - Google/Apple: 모든 인앱에서 disallowed_useragent로 100% 차단
 * - Kakao OAuth in KakaoTalk in-app: 같은 앱 내부 OAuth → 무한 리다이렉트 사례
 * - LINE OAuth in LINE in-app: 동일 이슈 (같은 앱 내부 OAuth 충돌)
 * - Naver OAuth in Naver in-app: 동일 이슈
 */
function isProviderUnsafe(
  provider: OAuthProvider,
  type: InAppBrowserType,
): boolean {
  if (provider === "google" || provider === "apple") return true;
  if (provider === "kakao" && type === "kakaotalk") return true;
  if (provider === "line" && type === "line") return true;
  if (provider === "naver" && type === "naver") return true;
  return false;
}

/**
 * 외부 브라우저에서 열 대상 URL을 구성.
 *
 * - 현재 페이지가 이미 `/login`이면 currentUrl 그대로 (로그인 페이지에서 외부 브라우저 이동)
 * - 모달/상품/예약 페이지에서 호출된 경우엔 `/login?returnUrl=<currentUrl>` 로 변환
 *   → 외부 브라우저가 곧장 로그인 화면을 띄우고, OAuth 후 원래 페이지로 복귀
 */
function buildExternalTargetUrl(currentUrl: string): string {
  try {
    const url = new URL(currentUrl);
    const segments = url.pathname.split("/").filter(Boolean);
    // pathname 예: /ko/login, /en/login → segments[1] === "login"
    const isLoginPath = segments[1] === "login";
    if (isLoginPath) return currentUrl;

    const locale = segments[0] || "ko";
    const returnUrl = encodeURIComponent(`${url.pathname}${url.search}`);
    return `${url.origin}/${locale}/login?returnUrl=${returnUrl}`;
  } catch {
    return currentUrl;
  }
}

/**
 * 인앱 브라우저에서 OAuth 시도 전 사전 검사.
 *
 * 분기:
 * - 인앱이 아니면 그대로 진행
 * - "위험한 조합"이면 시스템 브라우저로 우회 (불가능하면 가이드 모달)
 * - iOS Meta 계열(Instagram/Facebook/Threads/Twitter)은 무조건 가이드 모달
 *   (코드 강제 이동 불가)
 * - 그 외(예: 카톡 인앱 + Naver OAuth)는 정상 진행 — 자체 OAuth는 동작 가능
 *
 * @returns
 * - "proceed": OAuth 정상 진행
 * - "auto-redirect": 시스템 브라우저로 즉시 이동 (반환된 url 사용)
 * - "show-guide": 가이드 모달 노출 (코드로 강제 이동 불가)
 */
export function checkInAppForOAuth(
  provider: OAuthProvider,
  currentUrl: string,
): InAppPrecheckResult {
  const type = getInAppBrowserType();
  if (type === "unknown") return { action: "proceed" };

  const unsafe = isProviderUnsafe(provider, type);
  // 외부 브라우저로 갈 때는 곧장 로그인 페이지를 띄우도록 URL 가공.
  // (모달/상품 페이지에서 호출된 경우에도 외부에선 로그인 화면이 노출됨)
  const targetUrl = buildExternalTargetUrl(currentUrl);
  const plan = getOpenExternalPlan(targetUrl);

  // 위험한 조합: 무조건 외부로
  if (unsafe) {
    if (plan?.type === "auto-redirect" && plan.url) {
      return { action: "auto-redirect", url: plan.url };
    }
    return { action: "show-guide" };
  }

  // iOS Meta 인앱: 자체 OAuth라도 외부 브라우저 권장 (자동 이동 불가 → 가이드)
  if (isMetaInAppBrowser()) {
    return { action: "show-guide" };
  }

  return { action: "proceed" };
}
